import { useState, useEffect } from "react";
import { authFetch } from "../api/authApi";

const PROJECTS_CACHE_KEY = "projectsPicklistCache";
const PROJECTS_CACHE_TTL_MS = 5 * 60 * 1000;

let projectsInflightPromise = null;
let projectsMemoryCache = {
  loadedAt: 0,
  picklist: [],
};

function readProjectsCache() {
  try {
    const raw = window.localStorage.getItem(PROJECTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const loadedAt = Number(parsed?.loadedAt || 0);
    const picklist = Array.isArray(parsed?.picklist) ? parsed.picklist : [];
    if (!loadedAt) return null;
    return { loadedAt, picklist };
  } catch {
    return null;
  }
}

function writeProjectsCache(picklist) {
  try {
    const next = {
      loadedAt: Date.now(),
      picklist: Array.isArray(picklist) ? picklist : [],
    };
    projectsMemoryCache = next;
    window.localStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage failures.
  }
}

export default function useProjects() {
  const [projects, setProjects] = useState(() => {
    const stored = readProjectsCache();
    return stored?.picklist || [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");

    const now = Date.now();
    if (
      Array.isArray(projectsMemoryCache.picklist) &&
      projectsMemoryCache.picklist.length > 0 &&
      now - Number(projectsMemoryCache.loadedAt || 0) <= PROJECTS_CACHE_TTL_MS
    ) {
      setProjects(projectsMemoryCache.picklist);
      setLoading(false);
      return Promise.resolve(projectsMemoryCache.picklist);
    }

    const stored = readProjectsCache();
    if (stored?.picklist?.length) {
      projectsMemoryCache = stored;
      if (now - stored.loadedAt <= PROJECTS_CACHE_TTL_MS) {
        setProjects(stored.picklist);
        setLoading(false);
        return Promise.resolve(stored.picklist);
      }
    }

    if (projectsInflightPromise) {
      return projectsInflightPromise;
    }

    projectsInflightPromise = authFetch("/oauth/projects/picklist")
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail =
            typeof data?.detail === "string"
              ? data.detail
              : typeof data?.detail?.message === "string"
                ? data.detail.message
                : "Failed to load FreshBooks projects";
          throw new Error(detail);
        }
        return data;
      })
      .then((data) => {
        const picklist = Array.isArray(data?.picklist) ? data.picklist : [];
        setProjects(picklist);
        writeProjectsCache(picklist);
        console.log("Projects:", picklist);
        setLoading(false);
        return picklist;
      })
      .catch((err) => {
        const rawMessage = err.message || "Failed to load FreshBooks projects";
        const normalized = /token document not found/i.test(rawMessage)
          ? "FreshBooks is not connected for this login yet. Click Connect FreshBooks once for this user."
          : rawMessage;

        const fallback = readProjectsCache();
        const fallbackPicklist = Array.isArray(fallback?.picklist) ? fallback.picklist : [];
        setProjects(fallbackPicklist);
        setError(normalized);
        setLoading(false);
        return fallbackPicklist;
      })
      .finally(() => {
        projectsInflightPromise = null;
      });

    return projectsInflightPromise;
  };

  useEffect(() => {
    load();
  }, []);

  return { projects, loading, error, reload: load };
}

